import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents } from '@pirouette/shared';
import { useAllFamilyBalances } from '@/data/billing';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/')({
  component: AdminOverview,
});

function useActiveDancerCount() {
  return useQuery({
    queryKey: ['kpi', 'active-dancers'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('dancers')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'active')
        .is('deleted_at', null);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

function useWeekClassCount() {
  return useQuery({
    queryKey: ['kpi', 'week-classes'],
    queryFn: async (): Promise<number> => {
      const start = new Date();
      const day = start.getDay();
      // Roll to most recent Monday.
      start.setDate(start.getDate() - ((day + 6) % 7));
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      const { count, error } = await supabase
        .from('class_sessions')
        .select('id', { count: 'exact', head: true })
        .gte('starts_at', start.toISOString())
        .lt('starts_at', end.toISOString());
      if (error) return 0;
      return count ?? 0;
    },
  });
}

function useUnreadMessageCount() {
  return useQuery({
    queryKey: ['kpi', 'unread-msgs'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .is('read_by_recipient_at', null);
      if (error) return 0;
      return count ?? 0;
    },
  });
}

function AdminOverview() {
  const dancers = useActiveDancerCount();
  const classes = useWeekClassCount();
  const unread = useUnreadMessageCount();
  const balances = useAllFamilyBalances();
  const totalOutstanding =
    balances.data?.reduce((s, b) => s + Math.max(0, b.outstanding_cents), 0) ?? 0;

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Studio overview
        </p>
        <h1 className="mt-2">Today</h1>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Active dancers" value={dancers.data ?? '—'} />
        <Kpi label="Classes this week" value={classes.data ?? '—'} />
        <Kpi label="Unread messages" value={unread.data ?? '—'} />
        <Kpi label="Outstanding balance" value={formatCents(totalOutstanding)} />
      </div>

      <section aria-labelledby="ops-heading" className="grid gap-3 sm:grid-cols-2">
        <h2 id="ops-heading" className="sr-only">
          Operational tools
        </h2>
        {[
          {
            to: '/admin/security' as const,
            title: 'Security',
            description: 'Two-factor authentication enrollment + status',
          },
          {
            to: '/admin/audit' as const,
            title: 'Audit log',
            description: 'Tamper-evident administrative write history (requires MFA)',
          },
        ].map((item) => (
          <Link key={item.to} to={item.to}>
            <article className="surface-elevated h-full p-5 hover:bg-[var(--color-surface-sunken)]">
              <h3 className="font-medium">{item.title}</h3>
              <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                {item.description}
              </p>
            </article>
          </Link>
        ))}
      </section>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <article className="surface-elevated p-5">
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{label}</p>
      <p className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums">{value}</p>
    </article>
  );
}
